/* Medicus — Fit Check (product pages)
 * ---------------------------------------------------------------------------
 * WHAT THIS DOES
 *   1. Swaps the "If In Doubt, Reach Out About Sizing" panel that YMQ renders
 *      beside the size buttons for a shorter card naming three routes to a
 *      right-sized cap: a Sample Box (the only guarantee), the Tailored Fit
 *      Check, and a finder. On the single-shape listings, where no such panel
 *      exists at all, the same card is INSERTED under the size buttons.
 *   2. Opens the finder over the page: the homepage quiz's four questions plus
 *      a head measurement, ending in a shape and a size.
 *
 * WHAT IT DELIBERATELY DOES NOT DO
 *   It never sets, checks, clears or submits anything in the YMQ form. The
 *   result NAMES the two buttons to tap and will scroll them into view; the
 *   choice stays the customer's. Every write path was left out on purpose —
 *   if you are adding one, that is a decision to take, not a detail to fix.
 *
 * WHERE THE NUMBERS COME FROM
 *   Nothing about sizing is hard-coded. The size bands are parsed out of the
 *   live YMQ button labels ("M/L Bandana (~54-57cm)"), and which sizes belong
 *   to which shape is read from YMQ's own conditional rules. Change a size in
 *   the YMQ app and this follows it. There is no second copy to keep in step.
 *
 * WHERE THE QUIZ LOGIC COMES FROM
 *   window.MCStyleFinderLogic, published by assets/mc-style-finder.js — the
 *   same questions, scores and notes as the homepage style finder, so the two
 *   can never give different answers. If that file is missing this one exits
 *   quietly and the original YMQ panel is left exactly as it was.
 * --------------------------------------------------------------------------- */
(function () {
  'use strict';

  /* --8<-- sizing:start — everything between the markers is unit-tested by
     test-sizing.mjs, which reads it straight out of this file. --8<-- */

  var INF = 999;

  /* Ordered: the first pattern that matches a label wins. */
  var SHAPE_RULES = [
    ['bandana',  /bandana/],
    ['captain',  /captain/],
    ['crown',    /crown/],
    ['duchess',  /duchess|dutchess/],
    ['pony',     /pony|ponytail/],
    ['braid',    /braid|briad/],          /* "Briad Pouch XL Hair" is live, and misspelt */
    ['bouffant', /bouffant/],
    ['turban',   /turban/],
    ['hijab',    /hijab/],
    ['ortho',    /hood|ortho/]
  ];

  /* Size buttons on some option sets name the Duchess by its fastening —
     "M/L Elastic (~54-57cm)" is the Duchess. Only consulted for size labels,
     because "elastic" also appears in the Crown's and Duchess's STYLE labels. */
  var SIZE_ONLY_RULES = [['duchess', /\belastic/]];

  /* Labels that sit in the size list but are not a size. */
  var NOT_A_SIZE = /not sure|sample box/i;

  function shapeFromLabel(label, isSizeLabel) {
    var s = String(label || '').toLowerCase();
    var rules = isSizeLabel ? SHAPE_RULES.concat(SIZE_ONLY_RULES) : SHAPE_RULES;
    for (var i = 0; i < rules.length; i++) if (rules[i][1].test(s)) return rules[i][0];
    return null;
  }

  /* "M/L Bandana (~54-57cm)" -> { min: 54, max: 57 }.  null when the label
     carries no head measurement at all, which is true and normal for the
     Braid Pouch, the Hood on some sets, and "One Size". */
  function parseBand(label) {
    var s = String(label || '')
      .toLowerCase()
      .replace(/[–—]/g, '-')      /* en and em dashes */
      .replace(/[~≈]/g, '')
      .replace(/\s+/g, ' ');
    var m;
    if ((m = s.match(/(\d+(?:\.\d+)?)\s*(?:cm)?\s*-\s*(\d+(?:\.\d+)?)\s*cm/))) {
      return { min: +m[1], max: +m[2] };
    }
    if ((m = s.match(/(\d+(?:\.\d+)?)\s*\+\s*cm\s*\+/))) {   /* "60+cm+" */
      return { min: +m[1], max: INF };
    }
    if ((m = s.match(/(\d+(?:\.\d+)?)\s*cm\s*\+/))) {        /* "63cm+" */
      return { min: +m[1], max: INF };
    }
    if ((m = s.match(/[<≤]\s*(\d+(?:\.\d+)?)\s*cm/))) { /* "(<51cm)" */
      return { min: 0, max: +m[1] };
    }
    if ((m = s.match(/[>≥]\s*(\d+(?:\.\d+)?)\s*cm/))) { /* "(>58cm)" */
      return { min: +m[1], max: INF };
    }
    return null;
  }

  /* entries: [{ vid, label, band }].  Returns a verdict, never a bare answer,
     because "between two sizes" and "this shape is not sized in cm" are both
     real outcomes that a single label would have to lie about. */
  function pickSize(entries, cm) {
    var withBand = entries.filter(function (e) { return e.band; });
    if (!withBand.length) return { kind: 'nocm', options: entries };

    var inside = withBand.filter(function (e) { return cm >= e.band.min && cm <= e.band.max; });
    if (inside.length) {
      /* Consecutive bands share their endpoints — 54cm is the top of the S/M
         and the bottom of the M/L. Where a measurement is in two bands, name
         the LARGER, because a cap has to go over the head before anything else
         about it matters; the smaller one is offered as the snugger option.
         Highest lower bound wins; a genuine nested pair falls back to the
         tighter range so the answer is still deterministic. */
      inside.sort(function (a, b) {
        if (b.band.min !== a.band.min) return b.band.min - a.band.min;
        return (a.band.max - a.band.min) - (b.band.max - b.band.min);
      });
      return { kind: 'exact', pick: inside[0], overlaps: inside.slice(1) };
    }

    var below = null, above = null;
    withBand.forEach(function (e) {
      if (e.band.max < cm && (!below || e.band.max > below.band.max)) below = e;
      if (e.band.min > cm && (!above || e.band.min < above.band.min)) above = e;
    });

    if (below && above) {
      var dB = cm - below.band.max, dA = above.band.min - cm;
      if (dB === dA) return { kind: 'between', below: below, above: above, pick: null };
      return { kind: 'gap', pick: dB < dA ? below : above, below: below, above: above };
    }
    if (below) return { kind: 'over', pick: below };
    if (above) return { kind: 'under', pick: above };
    return { kind: 'nocm', options: entries };
  }

  function toCm(value, unit) {
    var n = parseFloat(value);
    if (!isFinite(n)) return null;
    return unit === 'in' ? n * 2.54 : n;
  }

  /* Sanity bounds. An adult head is roughly 51-63cm; anything outside this is
     a typo (or inches typed into the cm box), not a cap we can size. */
  var CM_MIN = 40, CM_MAX = 80;
  function validCm(cm) { return cm != null && cm >= CM_MIN && cm <= CM_MAX; }

  /* --8<-- sizing:end --8<-- */

  /* ---- Reading the YMQ form (read-only, always) --------------------------- */

  /* The three option sets that carry the old "If In Doubt" panel today. The
     header text is checked first, so renaming an option in YMQ does not break
     this; the ids are the belt to that braces. */
  var PANEL_IDS = ['558tem25', '536tem33', '13tem24'];
  var PANEL_TEXT = /if in doubt/i;

  function all(sel, root) { return [].slice.call((root || document).querySelectorAll(sel)); }
  function optLabel(box) { return box.getAttribute('ymq-option-label') || ''; }
  function optType(box) { return box.getAttribute('ymq-option-type') || ''; }
  function optId(box) { return box.getAttribute('ymq-option-id') || ''; }
  function inputs(box) { return all('input[ymq-option-value-id]', box); }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function findPanel() {
    var boxes = all('.ymq-options-box[ymq-option-type="11"]');
    for (var i = 0; i < boxes.length; i++) {
      var head = boxes[i].querySelector('.ymq-option-header');
      if (head && PANEL_TEXT.test(head.textContent || '')) return boxes[i];
      if (PANEL_IDS.indexOf(optId(boxes[i])) > -1) return boxes[i];
    }
    return null;
  }

  /* The "Above ear considerations" block YMQ shows for the Captain and the
     Crown. Header text first, ids as the belt to those braces. It is never
     removed: the stylesheet collapses it, and its display value — which YMQ
     alone sets — is read back as the truth about which shape is chosen. */
  var EAR_IDS = ['524tem27', '558tem26', '536tem31'];
  var EAR_TEXT = /above ear/i;
  var EAR_SHAPES = ['captain', 'crown'];

  function findEarBlock() {
    var boxes = all('.ymq-options-box[ymq-option-type="11"]');
    for (var i = 0; i < boxes.length; i++) {
      var b = boxes[i];
      if (b.hasAttribute('data-mc-fit-mounted')) continue;
      var head = b.querySelector('.ymq-option-header');
      if (head && EAR_TEXT.test(head.textContent || '')) return b;
      if (EAR_IDS.indexOf(optId(b)) > -1) return b;
      if (EAR_TEXT.test(b.textContent || '')) return b;
    }
    return null;
  }

  function isShown(node) {
    try { return !!node && getComputedStyle(node).display !== 'none'; } catch (e) { return false; }
  }

  /* Which shape the customer has chosen, read and never written. A radio in
     the style picker first; on a single-shape listing the size labels or the
     page's own handle say what the cap is. */
  function chosenShape(form) {
    if (form.styleBox) {
      var on = inputs(form.styleBox).filter(function (inp) { return inp.checked; })[0];
      return on ? shapeFromLabel(on.value, false) : null;
    }
    var seen = {};
    if (form.sizeBox) {
      inputs(form.sizeBox).forEach(function (inp) {
        if (NOT_A_SIZE.test(inp.value)) return;
        var k = shapeFromLabel(inp.value, true);
        if (k) seen[k] = true;
      });
    }
    var keys = Object.keys(seen);
    if (keys.length === 1) return keys[0];
    var m = (location.pathname.match(/\/products\/([^\/?#]+)/) || [])[1] || '';
    if (/captain/.test(m)) return 'captain';
    if (/\bcrown\b|the-crown/.test(m)) return 'crown';
    return null;
  }

  function findSizeBox() {
    var boxes = all('.ymq-options-box');
    for (var i = 0; i < boxes.length; i++) {
      if (/scrub cap size/i.test(optLabel(boxes[i])) && inputs(boxes[i]).length) return boxes[i];
    }
    return null;
  }

  /* The style picker, not the "Tailored Fit Check" or any of the embroidery
     options that also have "size" or "style" in their name. The test that
     actually settles it is whether its values read as cap shapes. */
  function findStyleBox() {
    var boxes = all('.ymq-options-box[ymq-option-type="5"]');
    for (var i = 0; i < boxes.length; i++) {
      var b = boxes[i], lab = optLabel(b);
      if (!/fit|style/i.test(lab)) continue;
      if (/best fit|tailored|text|embroid|vinyl|logo|placement|colour|color|font/i.test(lab)) continue;
      var shaped = inputs(b).filter(function (inp) { return shapeFromLabel(inp.value, false); });
      if (shaped.length) return b;
    }
    return null;
  }

  function findBestFitBox() {
    var boxes = all('.ymq-options-box');
    for (var i = 0; i < boxes.length; i++) {
      if (/best fit|tailored fit/i.test(optLabel(boxes[i]))) return boxes[i];
    }
    return null;
  }

  /* ---- Best Fit Check, moved up to the size row (4 Sept 2026) -------------
     Matt: it has to sit "where the sizing choosing happens to make it clearer
     that it's an option". YMQ gives the theme no way to order one option box
     against another, so the box itself is moved to sit directly after the
     sizing card. NOTHING about the option changes — same box, same label,
     same radio inputs, same YMQ price and conditions — it is the same node in
     a different place, so the app reads it exactly as it did before. Delete
     this function and the app's own order comes back.

     It re-runs on YMQ's re-renders (see queueEar), because a re-render puts
     the box back where the app had it. */
  function moveBestFit() {
    var box = findBestFitBox();
    if (!box) return;
    /* 5 Sept 2026, Matt: "have the fit check go inside the 'Not sure on size?'
       block". The card now carries a slot for it, between the finder caption
       and the footer links, and the box is moved INTO the card. Same node,
       same inputs, same YMQ price and conditions; it only lives inside the
       card's markup now, which sits in the same form. The card's old "Or have
       us check your measurements" link is dropped once the box is there,
       because the option itself is what the link pointed at. */
    var card = document.querySelector('[data-mc-fit-card]');
    if (card && card.closest('form') === box.closest('form') && !box.contains(card)) {
      var slot = card.querySelector('.mc-fit__check');
      if (!slot) {
        slot = el('div', 'mc-fit__check');
        var foot = card.querySelector('.mc-fit__foot');
        if (foot) card.insertBefore(slot, foot); else card.appendChild(slot);
      }
      var link = card.querySelector('.mc-fit__link');
      if (link) link.parentNode.removeChild(link);
      if (slot.firstElementChild !== box) {
        slot.appendChild(box);
        if (!box.hasAttribute('data-mc-fit-moved')) box.setAttribute('data-mc-fit-moved', '');
      }
      moveFitFields(box, slot);
      return;
    }
    /* No card (finder logic missing, or a listing laid out differently):
       the 4 Sept behaviour, box directly after the sizing card. */
    var anchor = document.querySelector('[data-mc-fit-mounted]') || findSizeBox();
    if (!anchor || anchor === box || box.contains(anchor) || anchor.contains(box)) return;
    if (anchor.nextElementSibling === box) return;                 /* already there */
    if (anchor.parentNode !== box.parentNode) return;              /* another column: leave it */
    anchor.parentNode.insertBefore(box, anchor.nextSibling);
    if (!box.hasAttribute('data-mc-fit-moved')) box.setAttribute('data-mc-fit-moved', '');
  }

  /* The measurement form that choosing the check reveals (6 Sept 2026).
     Matt: "when they select the best fit check we should have these options
     show below it so we don't get confused as to where the form is". YMQ keeps
     the eight boxes (units, circumference, forehead-to-nape, ear-to-ear, hair,
     coverage, ponytail, agreement) where the app listed them, further down the
     column, and shows them by condition when the check is chosen. Which boxes
     they are is read from the app's own rules — every option whose condition
     names the check's option id — never from labels. They are moved, in their
     own order, into a slot right under the check tile. Same nodes, so YMQ's
     show/hide and the line-item properties are untouched. */
  function moveFitFields(box, slot) {
    var oid = optId(box);
    var setNo = (oid.match(/^(\d+)tem/) || [])[1];
    var cfg = window.ymq_option;
    if (!setNo || !cfg || !cfg.option_sets) return;
    var cond = (cfg.option_sets['tem' + setNo] || {}).condition;
    if (!cond) return;
    var ids = [];
    Object.keys(cond).forEach(function (target) {
      var rules = (cond[target] || {}).conditions; if (!rules) return;
      var hit = false;
      Object.keys(rules).forEach(function (g) {
        var grp = rules[g] || {};
        Object.keys(grp).forEach(function (k) { if (grp[k] && grp[k].id === oid) hit = true; });
      });
      if (hit && /^\d+tem\d+$/.test(target)) ids.push(target);
    });
    if (!ids.length) return;
    var form = box.closest('form');
    var boxes = all('.ymq-options-box[ymq-option-id]').filter(function (b) {
      return ids.indexOf(b.getAttribute('ymq-option-id')) !== -1 && b !== box && !b.contains(box) && b.closest('form') === form;
    });
    if (!boxes.length) return;
    var fields = slot.querySelector('.mc-fit__fields');
    if (!fields) { fields = el('div', 'mc-fit__fields'); slot.appendChild(fields); }
    boxes.forEach(function (b) {
      if (b.parentNode === fields) return;
      fields.appendChild(b);                       /* document order is the app's order */
    });
  }

  /* "I'm Not Sure" on the size buttons (5 Sept 2026): Matt wants it lightly
     shaded with a small link icon, because it is the route to a sample box
     rather than a size. A data attribute on the item, styled in
     mc-fit-check.css; the input and its value are untouched. Guarded so the
     body observer is not woken by a no-op write. */
  function markNotSure() {
    all('.mc-fit-sizebox .ymq-option-button-input').forEach(function (inp) {   /* size row only: the returns box has an "Order Sample Box Instead" too */
      if (!/not\s*sure|sample box/i.test(inp.value || '')) return;
      var item = inp.closest('.ymq-option-value-item');
      if (item && !item.hasAttribute('data-mc-notsure')) item.setAttribute('data-mc-notsure', '');
    });
  }

  /* YMQ keeps its conditional rules in window.ymq_option. Reading them is how
     we know that "One Size" belongs to the Hijab and the Turban rather than
     guessing from its name — which would be impossible.

     ONLY TYPE 1 RULES SAY WHAT A STYLE COMES IN (fixed 5 Sept 2026).
     `condition` is keyed by whatever carries a rule, mixing option ids
     (524tem25) with value ids (524tem1_8), and each rule has a mode:

       type 1  show / hide   — "this size exists for that style"
       type 2  availability  — "this size is selectable for that style",
                               rendered greyed with `disabled_text` when the
                               condition fails

     Reading a type 2 rule as an exhaustive list is what sent a 58cm head to
     the "Small / Medium Captain (<53cm)" button on the Captain listing: set
     524 carries exactly one size rule, a type 2 on S/M, so the allowed list
     came back one item long and pickSize honestly called 58cm "over".

     So the two are kept apart. `show` is an allow-list and may stand alone.
     `avail` is read as an addition only — it is what tells us "One Size"
     belongs to the Turban and the Hijab, which no label could — and it is
     never allowed to remove a size the buttons themselves name. sizeLine's
     cross-check below catches whatever this app does next. */
  function conditionMap(sizeBox, styleBox) {
    var out = null;
    try {
      var setKey = (optId(sizeBox).match(/^(\d+)tem\d+$/) || [])[1];
      var styleOptId = styleBox ? optId(styleBox) : '';
      var cfg = window.ymq_option;
      if (!setKey || !styleOptId || !cfg || !cfg.option_sets) return null;
      var cond = (cfg.option_sets['tem' + setKey] || {}).condition;
      if (!cond) return null;
      out = { show: {}, avail: {} };
      Object.keys(cond).forEach(function (sizeVid) {
        var rule = cond[sizeVid] || {};
        var bucket = String(rule.type) === '1' ? out.show
                   : String(rule.type) === '2' ? out.avail
                   : null;
        if (!bucket) return;
        var rules = rule.conditions;
        if (!rules) return;
        Object.keys(rules).forEach(function (g) {
          var grp = rules[g];
          Object.keys(grp).forEach(function (k) {
            var r = grp[k];
            if (!r || r.id !== styleOptId) return;
            String(r.value || '').split(',').forEach(function (styleVid) {
              if (!styleVid) return;
              (bucket[styleVid] || (bucket[styleVid] = [])).push(sizeVid);
            });
          });
        });
      });
    } catch (e) { return null; }
    return out;
  }

  /* Every size button that belongs to one shape, with its cm band parsed off
     the label. Three sources in order of trust: YMQ's own rules, the shape
     named in the label, then "this listing only makes one shape anyway". */
  /* ignoreRules: build the list from the buttons alone, ignoring whatever YMQ's
     condition data claims. sizeLine uses it as a second opinion.

     NOT FILTERED BY VISIBILITY, and that was tried and reverted on 5 Sept.
     On a listing that offers several shapes, YMQ hides the sizes belonging to
     every shape except the one currently selected — and the finder's whole job
     is to name a shape the customer has not selected yet. Filtering on display
     emptied the Captain's sizes on the Classic Heart listing and turned a good
     answer into "this listing does not carry a size for that shape". The sizes
     are there; they appear when the recommended style is tapped. */
  function sizeEntriesFor(form, shapeKey, ignoreRules) {
    if (!form.sizeBox) return [];
    var rows = inputs(form.sizeBox)
      .filter(function (inp) { return !NOT_A_SIZE.test(inp.value); })
      .map(function (inp) {
        return {
          vid: inp.getAttribute('ymq-option-value-id'),
          label: inp.value,
          band: parseBand(inp.value)
        };
      });

    var styleVid = form.styleVidByShape[shapeKey];
    var cond = (!ignoreRules && form.cond) ? form.cond : null;

    /* A show rule is YMQ saying this size exists for this style and no other.
       That one may stand alone. */
    if (cond && styleVid && cond.show[styleVid]) {
      var allowed = cond.show[styleVid];
      var byRule = rows.filter(function (r) { return allowed.indexOf(r.vid) > -1; });
      if (byRule.length) return byRule;
    }

    var byName = rows.filter(function (r) { return shapeFromLabel(r.label, true) === shapeKey; });

    /* Availability rules are added to what the labels already say, never
       subtracted from it, and only for rows whose label names no shape at all.
       That is "One Size" on the Turban and the Hijab, and nothing else. */
    if (cond && styleVid && cond.avail[styleVid]) {
      var av = cond.avail[styleVid];
      rows.forEach(function (r) {
        if (av.indexOf(r.vid) < 0) return;
        if (shapeFromLabel(r.label, true)) return;
        if (byName.indexOf(r) > -1) return;
        byName.push(r);
      });
    }
    if (byName.length) return byName;

    /* A single-shape listing: no conditions, no shape names in the labels. */
    if (form.shapes.length === 1 && form.shapes[0] === shapeKey) return rows;
    return [];
  }

  function readForm() {
    var sizeBox = findSizeBox();
    var styleBox = findStyleBox();
    if (!sizeBox && !styleBox) return null;

    var styleVidByShape = {}, styleLabelByShape = {}, shapes = [];
    if (styleBox) {
      inputs(styleBox).forEach(function (inp) {
        var k = shapeFromLabel(inp.value, false);
        if (!k || styleVidByShape[k]) return;
        styleVidByShape[k] = inp.getAttribute('ymq-option-value-id');
        styleLabelByShape[k] = inp.value;
        shapes.push(k);
      });
    }
    return {
      sizeBox: sizeBox,
      styleBox: styleBox,
      bestFitBox: findBestFitBox(),
      shapes: shapes,
      styleVidByShape: styleVidByShape,
      styleLabelByShape: styleLabelByShape,
      cond: (sizeBox && styleBox) ? conditionMap(sizeBox, styleBox) : null,   /* { show, avail } */
      sizeName: sizeBox ? optLabel(sizeBox) : 'Scrub Cap Size',
      styleName: styleBox ? optLabel(styleBox) : ''
    };
  }

  /* ---- The card ----------------------------------------------------------- */

  function scrollTo(node) {
    if (!node) return;
    try { node.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
    catch (e) { node.scrollIntoView(); }
    var title = node.querySelector('.ymq-option-title, .ymq_lable');
    if (title) {
      title.setAttribute('tabindex', '-1');
      try { title.focus({ preventScroll: true }); } catch (e) { /* focus is a nicety */ }
    }
  }

  /* ---- Ear to ear, for the two shapes that sit above the ears ------------ */

  function buildEar(cfg, shape) {
    var e = cfg.ear, c = cfg.copy;
    var wrap = el('div', 'mc-fit__ear');
    wrap.setAttribute('data-mc-fit-ear', shape || 'both');
    wrap.appendChild(el('h4', 'mc-fit__ear-head', e.heading));

    var row = el('div', 'mc-fit__ear-row');
    if (e.photo) {
      var img = el('img', 'mc-fit__ear-photo');
      img.src = e.photo; img.alt = e.photo_alt || ''; img.loading = 'lazy'; img.width = 96; img.height = 98;
      row.appendChild(img);
    }
    var name = shape && cfg.styles.filter(function (s) { return s.key === shape; })[0];
    var text = name ? String(e.text).replace('{shape}', name.name) : e.text_both;
    row.appendChild(el('p', 'mc-fit__ear-text', text));
    wrap.appendChild(row);

    var keys = shape && e.shapes[shape] ? [shape] : EAR_SHAPES.filter(function (k) { return e.shapes[k]; });
    var table = el('table', 'mc-fit__ear-table');
    var thead = el('thead'), hr = el('tr');
    hr.appendChild(el('th', null, e.col_size));
    keys.forEach(function (k) {
      var st = cfg.styles.filter(function (s) { return s.key === k; })[0];
      hr.appendChild(el('th', null, keys.length > 1 && st ? st.name : e.col_measure));
    });
    thead.appendChild(hr); table.appendChild(thead);
    var tbody = el('tbody');
    var rows = e.shapes[keys[0]] || [];
    rows.forEach(function (r, i) {
      var cells = keys.map(function (k) { return ((e.shapes[k] || [])[i] || [])[1] || ''; });
      /* A size the shape is not made in — Captain has no XS — prints nothing
         at all rather than a row of dashes. */
      if (!cells.filter(Boolean).length) return;
      var tr = el('tr');
      tr.appendChild(el('th', null, r[0]));
      cells.forEach(function (c) { tr.appendChild(el('td', null, c || '\u2013')); });
      tbody.appendChild(tr);
    });
    table.appendChild(tbody);
    wrap.appendChild(table);
    return wrap;
  }

  /* Show the ear-to-ear block only while the chosen shape sits above the
     ears. Two witnesses: YMQ's own conditional block being displayed (it
     knows the rules), and the style radio that is checked. Either is enough.
     Re-run on every form mutation; cheap, and it only touches the card. */
  function updateEar(cfg, form) {
    var card = document.querySelector('[data-mc-fit-card]');
    if (!card || !cfg.ear || !cfg.ear.shapes) return;
    var block = findEarBlock();
    if (block && !block.classList.contains('mc-ear-src')) block.classList.add('mc-ear-src');
    var shape = chosenShape(form);
    var key = '';
    if (shape) key = EAR_SHAPES.indexOf(shape) > -1 ? shape : '';       /* the radio settles it */
    else if (block && isShown(block)) key = 'both';                      /* YMQ says above-ears, shape unknown */
    var have = card.querySelector('[data-mc-fit-ear]');
    if (have && have.getAttribute('data-mc-fit-ear') === key) return;
    if (have) have.parentNode.removeChild(have);
    if (!key) return;
    var node = buildEar(cfg, key === 'both' ? null : key);
    /* After the sample box, never before it: the sample box is the only
       guarantee on the card and stays the first thing read. */
    var sample = card.querySelector('.mc-fit__sample');
    if (sample && sample.nextSibling) card.insertBefore(node, sample.nextSibling);
    else if (sample) card.appendChild(node);
    else card.appendChild(node);
  }

  /* The card is deliberately short. It is a signpost sitting beside the size
     buttons, not an essay: the sample box first because it is the only thing
     here that is a guarantee, the finder second, the paid check third as a
     single line. Anything that wanted a paragraph went inside the finder. */
  function buildCard(cfg, form, open) {
    var c = cfg.copy;
    var card = el('div', 'mc-fit');
    card.setAttribute('data-mc-fit-card', '');
    card.appendChild(el('h3', 'mc-fit__head', c.card_heading));
    card.appendChild(el('p', 'mc-fit__lede', c.card_lede));

    var sample = el('a', 'mc-fit__sample');
    sample.href = cfg.links.sample;
    sample.setAttribute('data-mc-event', 'fit_check_sample');
    sample.appendChild(el('span', 'mc-fit__stitle', c.sample_heading));
    sample.appendChild(el('span', 'mc-fit__stext', c.sample_text));
    card.appendChild(sample);

    var go = el('button', 'mc-fit__cta', c.quiz_cta);
    go.type = 'button';
    go.setAttribute('data-mc-fit-open', '');
    go.addEventListener('click', open);
    card.appendChild(go);
    card.appendChild(el('p', 'mc-fit__caption', c.quiz_text));

    /* Only offered where the option actually exists on the page. */
    if (form.bestFitBox) {
      var goto = el('button', 'mc-fit__link', c.check_cta);
      goto.type = 'button';
      goto.addEventListener('click', function () { scrollTo(form.bestFitBox); });
      card.appendChild(goto);
    }

    var foot = el('p', 'mc-fit__foot');
    var a1 = el('a', null, c.foot_guide); a1.href = cfg.links.sizing;
    var a2 = el('a', null, c.foot_measure); a2.href = cfg.links.measure;
    foot.appendChild(a1);
    foot.appendChild(document.createTextNode('  \u00b7  '));
    foot.appendChild(a2);
    card.appendChild(foot);

    return card;
  }

  /* Replace the old panel's contents where there is one, and add the card
     under the size buttons on the listings that never had one. Either way the
     YMQ box itself is left in place, so YMQ's own column widths still apply. */
  function mountCard(cfg, form, open) {
    var card = buildCard(cfg, form, open);
    /* Marks the size option so the stylesheet can stop it and the panel sharing
       a 132px column on a phone. A class, nothing else — no value is touched. */
    if (form.sizeBox) form.sizeBox.classList.add('mc-fit-sizebox');
    var panel = findPanel();
    if (panel) {
      var host = panel.querySelector('.ymq-option-custome-text') || panel;
      host.innerHTML = '';
      host.appendChild(card);
      host.classList.add('mc-fit-wrap');                     /* container for the card's wide layout */
      panel.setAttribute('data-mc-fit-mounted', 'replaced');
      return true;
    }
    if (!form.sizeBox) return false;
    var box = el('div', 'ymq-options-box ymq-option-col-100 mc-fit-box mc-fit-wrap');
    box.setAttribute('data-mc-fit-mounted', 'added');
    box.appendChild(card);
    form.sizeBox.parentNode.insertBefore(box, form.sizeBox.nextSibling);
    return true;
  }

  /* ---- The finder --------------------------------------------------------- */

  function publish(name, payload) {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish(name, payload);
      }
    } catch (e) { /* tracking must never break the page */ }
  }

  var FOCUSABLE = 'a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])';

  function Finder(cfg, form, L) {
    var c = cfg.copy;
    var byKey = {};
    cfg.styles.forEach(function (s) { byKey[s.key] = s; });

    var answers = {}, path = [], forced = null, unit = 'cm', lastOpener = null;

    var root = el('div', 'mc-fq');
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', c.quiz_title);
    var scrim = el('div', 'mc-fq__scrim');
    var panel = el('div', 'mc-fq__panel');
    var bar = el('div', 'mc-fq__bar');
    bar.appendChild(el('h2', 'mc-fq__title', c.quiz_title));
    var close = el('button', 'mc-fq__close');
    close.type = 'button';
    close.setAttribute('aria-label', c.quiz_close);
    close.innerHTML = '<span aria-hidden="true">&times;</span>';
    bar.appendChild(close);
    var body = el('div', 'mc-fq__body');
    panel.appendChild(bar);
    panel.appendChild(body);
    root.appendChild(scrim);
    root.appendChild(panel);
    document.body.appendChild(root);

    scrim.addEventListener('click', hide);
    close.addEventListener('click', hide);
    document.addEventListener('keydown', function (e) {
      if (!root.classList.contains('is-open')) return;
      if (e.key === 'Escape') { e.preventDefault(); hide(); return; }
      if (e.key !== 'Tab') return;
      var f = all(FOCUSABLE, panel).filter(function (n) { return n.offsetParent !== null; });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    function show() {
      lastOpener = document.activeElement;
      answers = {}; path = []; forced = null;
      root.classList.add('is-open');
      document.body.classList.add('mc-fq-locked');
      renderQuestion(0);
      publish('fit_check_open', { shapes: form.shapes.join(',') });
    }

    function hide() {
      root.classList.remove('is-open');
      document.body.classList.remove('mc-fq-locked');
      if (lastOpener && lastOpener.focus) { try { lastOpener.focus(); } catch (e) { /* nothing to return to */ } }
    }

    function askable() {
      var n = [];
      for (var i = 0; i < L.QUESTIONS.length; i++) {
        var q = L.QUESTIONS[i];
        if (typeof q.skipIf === 'function' && q.skipIf(answers)) continue;
        n.push(i);
      }
      return n;
    }

    function nextIndex(from) {
      for (var i = from; i < L.QUESTIONS.length; i++) {
        var q = L.QUESTIONS[i];
        if (typeof q.skipIf === 'function' && q.skipIf(answers)) continue;
        return i;
      }
      return -1;
    }

    /* The measurement is one more step on the end, so it counts in the total. */
    function steps() { return askable().length + 1; }

    /* The total is not knowable on the first question (fixed 5 Sept 2026).
       askable() reads the answers so far, and "Do you want your hair tucked
       in?" is skipped for short hair — so the old counter opened at "Step 1 of
       5" and then said "Step 2 of 4", which reads like a bug even though both
       numbers were true when printed. On the opening question the denominator
       is left off; from the second it is settled and shown. Every branch in
       the current question set is decided by the first answer. If one is ever
       added further down, this is the line to widen. */
    function settledTotal() { return path.length > 0; }

    function head(shown) {
      var wrap = el('div');
      var label = settledTotal()
        ? c.quiz_step.replace('{n}', String(shown)).replace('{of}', String(steps()))
        : (c.quiz_step_open || 'Step {n}').replace('{n}', String(shown));
      wrap.appendChild(el('p', 'mc-fq__count', label));
      var track = el('div', 'mc-fq__bar-track');
      track.setAttribute('aria-hidden', 'true');
      var fill = el('span', 'mc-fq__bar-fill');
      fill.style.width = Math.round((shown - 1) / steps() * 100) + '%';
      track.appendChild(fill);
      wrap.appendChild(track);
      return wrap;
    }

    function focusHeading(node) {
      node.setAttribute('tabindex', '-1');
      try { node.focus({ preventScroll: true }); } catch (e) { /* older browsers */ }
      body.scrollTop = 0;
    }

    function backButton() {
      if (!path.length) return null;
      var b = el('button', 'mc-fq__back');
      b.type = 'button';
      b.innerHTML = '<span aria-hidden="true">&larr;</span> ' + c.quiz_back;
      b.addEventListener('click', function () {
        var prev = path.pop();
        if (prev === 'measure') { renderMeasure(); return; }
        delete answers[L.QUESTIONS[prev].id];
        forced = null;
        renderQuestion(prev);
      });
      return b;
    }

    function renderQuestion(index) {
      var q = L.QUESTIONS[index];
      body.innerHTML = '';
      var asked = path.indexOf(index);
      var shown = (asked === -1 ? path.length : asked) + 1;
      body.appendChild(head(shown));

      var group = el('div');
      group.setAttribute('role', 'group');
      var legend = el('h3', 'mc-fq__legend', q.legend);
      legend.id = 'mc-fq-q' + index;
      group.setAttribute('aria-labelledby', legend.id);
      group.appendChild(legend);
      if (q.help) group.appendChild(el('p', 'mc-fq__help', q.help));

      var list = el('div', 'mc-fq__options');
      var chosen = q.multiple ? [] : null;
      q.options.forEach(function (opt) {
        var b = el('button', 'mc-fq__opt');
        b.type = 'button';
        b.appendChild(el('span', null, opt.label));
        if (q.multiple) {
          b.setAttribute('aria-pressed', 'false');
          b.addEventListener('click', function () {
            var on = b.getAttribute('aria-pressed') === 'true';
            b.setAttribute('aria-pressed', on ? 'false' : 'true');
            b.classList.toggle('is-on', !on);
            var at = chosen.indexOf(opt.v);
            if (on && at > -1) chosen.splice(at, 1);
            if (!on && at === -1) chosen.push(opt.v);
          });
        } else {
          b.addEventListener('click', function () { answer(index, opt.v); });
        }
        list.appendChild(b);
      });
      group.appendChild(list);
      body.appendChild(group);

      var foot = el('div', 'mc-fq__foot');
      if (q.multiple) {
        var go = el('button', 'mc-fq__go', q.continueLabel || c.quiz_continue);
        go.type = 'button';
        go.addEventListener('click', function () { answer(index, chosen.slice()); });
        foot.appendChild(go);
        if (q.noneLabel) {
          var none = el('button', 'mc-fq__ghost', q.noneLabel);
          none.type = 'button';
          none.addEventListener('click', function () { answer(index, []); });
          foot.appendChild(none);
        }
      }
      var back = backButton();
      if (back) foot.appendChild(back);
      if (foot.childNodes.length) body.appendChild(foot);
      focusHeading(legend);
    }

    function answer(index, value) {
      var q = L.QUESTIONS[index];
      answers[q.id] = value;
      if (path.indexOf(index) === -1) path.push(index);

      /* "A turban" or "a hijab" is not a preference to be scored — we cut a
         shape for exactly that, so the shape is settled and only the size is
         still open. */
      if (q.id === 'hair' && L.OVERRIDE[value]) { forced = L.OVERRIDE[value]; return renderMeasure(); }

      var next = nextIndex(index + 1);
      if (next === -1) return renderMeasure();
      renderQuestion(next);
    }

    function renderMeasure() {
      body.innerHTML = '';
      body.appendChild(head(steps()));

      var legend = el('h3', 'mc-fq__legend', c.measure_legend);
      body.appendChild(legend);
      body.appendChild(el('p', 'mc-fq__help', c.measure_help));

      /* Matt's own Short, loaded only when someone asks for it. No YouTube
         request is made — and no third-party cookie set — unless the button is
         pressed, which matters because this sits on a product page that is
         already carrying the YMQ option set. nocookie domain either way. */
      if (cfg.video && cfg.video.id) {
        var vwrap = el('div', 'mc-fq__video');
        var vbtn = el('button', 'mc-fq__videobtn');
        vbtn.type = 'button';
        vbtn.setAttribute('aria-expanded', 'false');
        vbtn.innerHTML = '<span class="mc-fq__play" aria-hidden="true">&#9654;</span>';
        vbtn.appendChild(document.createTextNode(c.measure_video_cta));
        var frame = null;
        vbtn.addEventListener('click', function () {
          if (frame) {
            frame.remove();
            frame = null;
            vbtn.setAttribute('aria-expanded', 'false');
            vbtn.lastChild.textContent = c.measure_video_cta;
            return;
          }
          frame = el('div', 'mc-fq__frame');
          var f = document.createElement('iframe');
          f.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(cfg.video.id) + '?rel=0&playsinline=1';
          f.title = cfg.video.title || c.measure_video_cta;
          f.loading = 'lazy';
          f.allow = 'accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen';
          f.setAttribute('allowfullscreen', '');
          f.referrerPolicy = 'strict-origin-when-cross-origin';
          frame.appendChild(f);
          vwrap.appendChild(frame);
          vbtn.setAttribute('aria-expanded', 'true');
          vbtn.lastChild.textContent = c.measure_video_close;
        });
        vwrap.appendChild(vbtn);
        body.appendChild(vwrap);
      }

      var wrap = el('div', 'mc-fq__measure');
      var input = el('input', 'mc-fq__num');
      input.type = 'text';
      input.inputMode = 'decimal';
      input.autocomplete = 'off';
      input.setAttribute('aria-label', c.measure_legend);
      input.placeholder = c.measure_placeholder;
      wrap.appendChild(input);

      var units = el('div', 'mc-fq__units');
      units.setAttribute('role', 'group');
      units.setAttribute('aria-label', c.measure_units);
      [['cm', 'cm'], ['in', 'in']].forEach(function (u) {
        var b = el('button', 'mc-fq__unit', u[1]);
        b.type = 'button';
        b.setAttribute('aria-pressed', unit === u[0] ? 'true' : 'false');
        b.addEventListener('click', function () {
          unit = u[0];
          all('.mc-fq__unit', units).forEach(function (x) {
            x.setAttribute('aria-pressed', x.textContent === u[1] ? 'true' : 'false');
          });
          input.focus();
        });
        units.appendChild(b);
      });
      wrap.appendChild(units);
      body.appendChild(wrap);

      var err = el('p', 'mc-fq__err');
      err.hidden = true;
      err.setAttribute('role', 'alert');
      body.appendChild(err);

      var foot = el('div', 'mc-fq__foot');
      var go = el('button', 'mc-fq__go', c.measure_cta);
      go.type = 'button';
      go.addEventListener('click', function () {
        var cm = toCm(input.value, unit);
        if (!validCm(cm)) {
          err.textContent = c.measure_error;
          err.hidden = false;
          input.focus();
          return;
        }
        if (path.indexOf('measure') === -1) path.push('measure');
        renderResult(cm);
      });
      foot.appendChild(go);

      var skip = el('button', 'mc-fq__ghost', c.measure_skip);
      skip.type = 'button';
      skip.addEventListener('click', function () {
        if (path.indexOf('measure') === -1) path.push('measure');
        renderResult(null);
      });
      foot.appendChild(skip);

      var back = backButton();
      if (back) foot.appendChild(back);
      body.appendChild(foot);

      var how = el('p', 'mc-fq__help');
      var a = el('a', null, c.measure_link);
      a.href = cfg.links.measure;
      how.appendChild(a);
      body.appendChild(how);

      input.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); go.click(); } });
      focusHeading(legend);
    }

    function fill(str, vars) {
      return String(str || '').replace(/\{(\w+)\}/g, function (m, k) {
        return (vars && vars[k] != null) ? vars[k] : m;
      });
    }

    function tapRow(label, value, note) {
      var row = el('div', 'mc-fq__row');
      row.appendChild(el('span', 'mc-fq__rowlab', label));
      row.appendChild(el('span', 'mc-fq__rowval', value));
      if (note) row.appendChild(el('span', 'mc-fq__rownote', note));
      return row;
    }

    /* Turns a verdict from pickSize into the one line a person can act on. */
    function sizeLine(shapeKey, cm) {
      var entries = sizeEntriesFor(form, shapeKey);
      var shapeName = (byKey[shapeKey] || {}).name || shapeKey;
      if (!entries.length) return { value: '—', note: c.size_none };
      if (cm == null) {
        return {
          value: entries.map(function (e) { return e.label; }).join('  ·  '),
          note: c.size_nomeasure
        };
      }
      var v = pickSize(entries, cm);

      /* THE BUTTONS ON THE PAGE WIN (5 Sept 2026). If the rule-filtered list
         puts the head off the end of the range while a size button that is on
         this page and named for this shape has a band containing the
         measurement, then the app's condition data was misread and the buttons
         are the truth. Never say "58cm is above the top of <smallest size>"
         with an XL sitting two rows below it. */
      if (v.kind !== 'exact') {
        var openList = sizeEntriesFor(form, shapeKey, true);
        if (openList.length > entries.length) {
          var v2 = pickSize(openList, cm);
          if (v2.kind === 'exact') { entries = openList; v = v2; }
        }
      }

      var shown = Math.round(cm * 10) / 10;
      if (v.kind === 'exact') {
        return {
          value: v.pick.label,
          note: v.overlaps.length
            ? fill(c.size_boundary, { cm: shown, other: v.overlaps[0].label })
            : fill(c.size_exact, { cm: shown })
        };
      }
      if (v.kind === 'gap') {
        return { value: v.pick.label, note: fill(c.size_gap, { cm: shown, a: v.below.label, b: v.above.label, pick: v.pick.label }) };
      }
      if (v.kind === 'between') {
        return { value: v.below.label + '  ' + c.size_or + '  ' + v.above.label,
                 note: fill(c.size_between, { cm: shown, a: v.below.label, b: v.above.label }) };
      }
      if (v.kind === 'under') return { value: v.pick.label, note: fill(c.size_under, { cm: shown, pick: v.pick.label }) };
      if (v.kind === 'over')  return { value: v.pick.label, note: fill(c.size_over,  { cm: shown, pick: v.pick.label }) };
      return {
        value: v.options.map(function (e) { return e.label; }).join('  ·  '),
        note: fill(c.size_nocm, { shape: shapeName })
      };
    }

    function altCard(key) {
      var s = byKey[key];
      if (!s) return null;
      var node = el(s.url ? 'a' : 'div', 'mc-fq__alt');
      if (s.url) { node.href = s.url; node.setAttribute('data-mc-event', 'fit_check_alt'); }
      node.appendChild(el('span', 'mc-fq__altname', s.name));
      if (s.summary) node.appendChild(el('span', 'mc-fq__altsum', s.summary));
      return node;
    }

    function renderResult(cm) {
      var keys = cfg.styles.map(function (s) { return s.key; });
      var ranked = forced ? [forced] : L.rank(answers, keys);
      var lead = ranked[0];
      var alts = ranked.slice(1, 3);
      var offered = form.shapes;
      var onListing = !offered.length || offered.indexOf(lead) > -1;
      var target = onListing ? lead : null;
      if (!target) {
        for (var i = 0; i < ranked.length && !target; i++) {
          if (offered.indexOf(ranked[i]) > -1) target = ranked[i];
        }
        if (!target) target = offered[0];
      }

      body.innerHTML = '';
      var wrap = el('div');
      wrap.setAttribute('role', 'status');

      var h = el('h3', 'mc-fq__reshead', forced ? c.result_direct : c.result_heading);
      wrap.appendChild(h);
      wrap.appendChild(el('p', 'mc-fq__ressub', c.result_sub));

      var leadStyle = byKey[lead] || {};
      var card = el('div', 'mc-fq__lead');
      card.appendChild(el('span', 'mc-fq__flag', c.result_flag));
      card.appendChild(el('p', 'mc-fq__name', leadStyle.name || lead));
      if (leadStyle.summary) card.appendChild(el('p', 'mc-fq__sum', leadStyle.summary));
      if (leadStyle.sizes) card.appendChild(el('p', 'mc-fq__meta', c.result_sizes + ' ' + leadStyle.sizes));
      if (!onListing) {
        card.appendChild(el('p', 'mc-fq__meta', fill(c.notlisting, { name: leadStyle.name || lead })));
        if (leadStyle.url) {
          var link = el('a', 'mc-fit__link', fill(c.notlisting_link, { name: leadStyle.name || lead }));
          link.href = leadStyle.url;
          link.setAttribute('data-mc-event', 'fit_check_other_listing');
          card.appendChild(link);
        }
      }
      wrap.appendChild(card);

      /* What to tap, on this page, in the words the buttons actually use. */
      if (target && (form.styleBox || form.sizeBox)) {
        var tap = el('div', 'mc-fq__tap');
        tap.appendChild(el('p', 'mc-fq__taphead', c.tap_heading));
        if (!onListing) tap.appendChild(el('p', 'mc-fq__rownote', fill(c.closest_here, { name: (byKey[target] || {}).name || target })));
        if (form.styleBox && form.styleLabelByShape[target]) {
          tap.appendChild(tapRow(form.styleName || c.tap_style, form.styleLabelByShape[target], null));
        }
        if (form.sizeBox) {
          var line = sizeLine(target, cm);
          tap.appendChild(tapRow(form.sizeName || c.tap_size, line.value, line.note));
        }
        var goto = el('button', 'mc-fq__go', c.tap_cta);
        goto.type = 'button';
        goto.style.marginTop = '12px';
        goto.addEventListener('click', function () {
          hide();
          scrollTo(form.styleBox || form.sizeBox);
        });
        tap.appendChild(goto);
        wrap.appendChild(tap);
      }

      if (alts.length) {
        wrap.appendChild(el('p', 'mc-fq__altshead', c.result_alts));
        var altWrap = el('div', 'mc-fq__alts');
        alts.forEach(function (k) { var n = altCard(k); if (n) altWrap.appendChild(n); });
        wrap.appendChild(altWrap);
      }

      var notes = [];
      if (!forced) {
        (L.NOTES || []).forEach(function (n) {
          try { if (n.when(answers, lead, alts)) notes.push(n.text); } catch (e) { /* a bad rule must not break the result */ }
        });
      }
      if (notes.length) {
        var noteWrap = el('div', 'mc-fq__notes');
        notes.forEach(function (t) { noteWrap.appendChild(el('p', 'mc-fq__note', t)); });
        wrap.appendChild(noteWrap);
      }

      var truth = el('p', 'mc-fq__truth');
      truth.appendChild(document.createTextNode(c.truth + ' '));
      var sample = el('a', null, c.truth_cta);
      sample.href = cfg.links.sample;
      sample.setAttribute('data-mc-event', 'fit_check_sample_result');
      truth.appendChild(sample);
      wrap.appendChild(truth);

      var actions = el('div', 'mc-fq__actions');
      var again = el('button', 'mc-fq__again');
      again.type = 'button';
      again.innerHTML = '<span aria-hidden="true">&#8634;</span> ' + c.quiz_again;
      again.addEventListener('click', function () { answers = {}; path = []; forced = null; renderQuestion(0); });
      actions.appendChild(again);
      wrap.appendChild(actions);

      body.appendChild(wrap);
      focusHeading(h);

      publish('fit_check_result', {
        shape: lead, applied_to: target || '', on_listing: onListing ? 'yes' : 'no',
        cm: cm == null ? '' : String(Math.round(cm * 10) / 10),
        hair: answers.hair || '', tuck: answers.tuck || '', sit: answers.sit || '',
        extras: Array.isArray(answers.extras) ? answers.extras.join(',') : ''
      });
    }

    return { open: show, close: hide };
  }

  /* ---- Wiring ------------------------------------------------------------- */

  function readConfig() {
    var node = document.querySelector('[data-mc-fit-config]');
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch (e) { return null; }
  }

  function mounted() {
    return !!document.querySelector('[data-mc-fit-card]');
  }

  /* Equal-width, equal-height option buttons. Marks every group that renders as
     buttons; the stylesheet does the rest. Swatch and image options are left
     alone because they are already a uniform grid. Adding a class is the whole
     of it — no value is read, set or cleared. */
  function uniformButtons() {
    all('.ymq-options-box .ymq-option-value-info').forEach(function (group) {
      if (group.classList.contains('mc-uniform')) return;
      if (!group.querySelector('.ymq-option-button-input')) return;
      group.classList.add('mc-uniform');
    });
  }

  function boot() {
    var cfg = readConfig();
    if (!cfg || !cfg.styles || !cfg.styles.length) return false;

    var L = window.MCStyleFinderLogic;
    if (!L || !L.QUESTIONS || typeof L.rank !== 'function') return false;   /* leave YMQ's own panel alone */

    var form = readForm();
    if (!form) return false;

    uniformButtons();

    var finder = Finder(cfg, form, L);
    var ok = mountCard(cfg, form, finder.open);
    if (ok) {
      earState = { cfg: cfg, form: form };
      try { updateEar(cfg, form); } catch (e) { /* the card stands without it */ }
      try { moveBestFit(); } catch (e) { /* it simply stays where YMQ put it */ }
      try { markNotSure(); } catch (e) { /* ignore */ }
    }
    return ok;
  }

  var earState = null, earQueued = false;
  function queueEar() {
    if (!earState || earQueued) return;
    earQueued = true;
    /* setTimeout, not requestAnimationFrame: rAF is frozen in a background
       tab, so a shape chosen before the tab is looked at would never redraw. */
    setTimeout(function () {
      earQueued = false;
      try { updateEar(earState.cfg, earState.form); } catch (e) { /* ignore */ }
      try { moveBestFit(); } catch (e) { /* ignore */ }
      try { markNotSure(); } catch (e) { /* ignore */ }
    }, 120);
  }

  /* YMQ renders its options after the page has loaded and re-renders them on
     its own schedule, so watch until the card is in place and keep watching in
     case a re-render takes it away again. */

  /* INP fix (7 Sept 2026). YMQ lazy-loads ~930 swatch images over the first
     minute and flips a class on each one as it lands; with five scripts
     watching document.body that was 24s of blocked main thread in the first
     33s of every product page (GSC: product group INP 490ms from 4 Sept).
     Only container-level changes matter to this script; value-level class
     flips, images, the header search box and our own widgets are ignored. */
  var MC_IGNORE_ATTR = '.ymq-option-value,.ymq-option-value-item,.ymq-option-swatches-item,.ymq-option-label,.ymq-option-button-input,img,input,textarea,.search-mega-menu__input';
  var MC_OWN = '[data-mc-fit-card],.mc-fit__check,.mc-emb-cue,.mc-emb-modal,[data-mc-satc],.mc-tellus-box,.mc-terms-box';
  function mcRelevant(records) {
    for (var i = 0; i < records.length; i++) {
      var m = records[i], t = m.target;
      if (t.nodeType !== 1) { t = t.parentElement; if (!t) continue; }
      if (t.closest && t.closest(MC_OWN)) continue;
      if (m.type === 'attributes' && t.matches && t.matches(MC_IGNORE_ATTR)) continue;
      return true;
    }
    return false;
  }

  function start() {
    if (mounted()) return;
    try { boot(); } catch (e) { /* the original panel stays exactly as it was */ }

    var tries = 0;
    var timer = setInterval(function () {
      tries++;
      try { uniformButtons(); } catch (e) { /* ignore */ }
      if (mounted() || tries > 60) { clearInterval(timer); return; }   /* 60 x 500ms = 30s */
      try { boot(); } catch (e) { clearInterval(timer); }
    }, 500);

    var mo = new MutationObserver(function (records) {
      if (!mcRelevant(records)) return;
      /* YMQ re-renders a group when a condition changes, which drops the class,
         so re-mark on every mutation whether or not the card is still there. */
      try { uniformButtons(); } catch (e) { /* ignore */ }
      if (mounted()) { queueEar(); return; }
      try { boot(); } catch (e) { /* ignore */ }
    });
    try { mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] }); } catch (e) { /* ignore */ }
    document.addEventListener('change', queueEar, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
